import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import CalculationForm from '../../components/CalculationForm';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import uuid from '@afosto/utils/uuid';
import { items as itemsApi } from '@afosto/order/api/Items';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import translations from './translations';

const CreateCalculation = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const history = useHistory();
  const intl = useIntl();
  const { orderID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [isFetchingOrder, setIsFetchingOrder] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOrder = async () => {
      setIsFetchingOrder(true);

      try {
        const response = await ordersApi.getOrder(orderID);
        const { data = null } = response;

        const itemsResponse = await itemsApi.listItems(
          orderID,
          {},
          {
            headers: {
              'x-page-size': 250,
            },
          },
        );

        const productSkus = (itemsResponse?.data ?? []).map(item => item.sku);
        const productResults = await fetchMultipleProducts(
          productSkus,
          {},
          {
            headers: {
              'x-page-size': 250,
            },
          },
        );
        const products = productResults.filter(product => product);

        const isReturn = data?.type === 'return';
        const formattedItems = (itemsResponse?.data ?? []).reduce((acc, item) => {
          const product = findObjectInArray(products, 'sku', item.sku);
          const generatedID = uuid();

          return [
            ...acc,
            {
              id: generatedID,
              reference: generatedID,
              sku: item.sku,
              description: item.description,
              amount: item.amount,
              isDiscounted: item.is_discounted,
              quantity: isReturn ? item.quantity * -1 : item.quantity,
              subtotal: item.subtotal,
              adjustments: item.adjustments ?? [],
              total: item.total,
              item_ids: item.ids,
              image: product?.image,
            },
          ];
        }, []);

        if (isMounted) {
          setOrder(data);
          setOrderItems(formattedItems);
        }
      } catch (err) {
        if (isMounted) {
          setOrder(null);
          setOrderItems([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingOrder(false);
        }
      }
    };

    if (orderID) {
      fetchOrder();
    }

    return () => {
      isMounted = false;
    };
  }, [orderID]);

  const handleCreateCalculation = async ({ items, adjustments, pricingAt, applyPricingRules }) => {
    try {
      const formattedItems = items.reduce((acc, item) => {
        const formattedAdjustments = (item?.adjustments ?? [])
          .filter(adjustment => !adjustment?.result?.is_applied_rule)
          .reduce(
            (adjustmentsAcc, adjustment) => [
              ...adjustmentsAcc,
              {
                reference: adjustment?.reference ?? uuid(),
                description: adjustment?.description,
                amount: adjustment?.amount,
                is_percentage: adjustment?.is_percentage,
                is_discount: adjustment?.is_discount,
              },
            ],
            [],
          );

        return [
          ...acc,
          {
            item_ids: item?.item_ids ?? [],
            description: item?.description,
            amount: item?.amount,
            is_discounted: item?.is_discounted ?? false,
            adjustments: formattedAdjustments,
            vat_percentage: item?.vat_percentage,
          },
        ];
      }, []);

      const response = await ordersApi.createCalculation(
        order?.id,
        {
          expires_at: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
          reference: order?.id,
          items: formattedItems,
          adjustments,
          ...(pricingAt ? { pricing_at: pricingAt } : {}),
        },
        { rules: applyPricingRules === false ? 0 : 1 },
      );
      const { data: calculation } = response;

      history.push(`/calculations/${calculation?.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.calculationNotCreated), {
        variant: 'error',
      });
    }
  };

  if (isFetchingOrder) {
    return <SkeletonPage breadcrumbs={3} />;
  }

  if (!order) {
    return <h1>No order found!</h1>;
  }

  if (order && order?.state?.is_finished) {
    return <h1>Order is already finished!</h1>;
  }

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent showActions={false} />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)}>
      <CalculationForm
        items={orderItems}
        order={order}
        onCancel={() => history.push(`/orders/${orderID}`)}
        onSubmit={handleCreateCalculation}
      />
    </Page>
  );
};

export default CreateCalculation;
